import { z } from 'zod';
import { API_URL } from '../../config/api';
import { getAuthHeader } from '../../config/auth';
import {
  FormDefinitionSchema,
  FormSummarySchema,
  FormAgentConversationBindingSchema,
  CreateFormRequestSchema,
  AgentApplyRequestSchema,
  AgentApplyResponseSchema,
  type Command,
  type FormDefinition,
  type FormSummary,
  type FormAgentConversationBinding,
  type CreateFormRequest,
  type AgentApplyRequest,
  type AgentApplyResponse,
  type ReducerResult,
} from '../contracts';

// Re-export so callers can import types from this module as before.
export type { FormSummary, CreateFormRequest };

// ---------------------------------------------------------------------------
// Typed failure shape — always `{ status: number; message: string }`.
// Never assign an array, object, or non-string to `message`.
// ---------------------------------------------------------------------------
type ApiFailure = { status: number; message: string };

function _normalizeErrorMessage(raw: string): string {
  // raw is the response body text.
  try {
    const parsed = JSON.parse(raw);
    const detail = parsed?.detail;

    if (typeof detail === 'string') {
      // FastAPI simple string error
      return detail;
    }
    if (Array.isArray(detail)) {
      // FastAPI validation-detail array: [{loc, msg, type}, ...]
      const first = detail[0];
      if (first && typeof first === 'object') {
        const loc = Array.isArray(first.loc) ? first.loc.join(' → ') : '';
        const msg = typeof first.msg === 'string' ? first.msg : '';
        return loc && msg ? `${loc}: ${msg}` : msg || loc || 'Validation error';
      }
      return 'Validation error';
    }
    if (detail && typeof detail === 'object') {
      // Structured detail with a string message field: {code, message}
      if (typeof detail.message === 'string') return detail.message;
    }
  } catch {
    // Non-JSON or empty body — fall through to generic message.
  }
  return raw.trim() || 'Request failed';
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: getAuthHeader(), ...options.headers },
  });
  const raw = await response.text();
  if (!response.ok) {
    const message = _normalizeErrorMessage(raw) || `Forms request failed (${response.status})`;
    throw { status: response.status, message } satisfies ApiFailure;
  }
  return raw ? (JSON.parse(raw) as T) : (undefined as T);
}

// ---------------------------------------------------------------------------
// Owner API functions — Zod parses both directions
// ---------------------------------------------------------------------------

export async function listFormSummaries(): Promise<FormSummary[]> {
  const raw = await request<unknown>('/api/forms');
  return z.array(FormSummarySchema).parse(raw);
}

// Production 2026-10-08: a "New form" POST never reached the server and the
// button stayed on "Creating…" until the user clicked again. Bound each attempt
// and resend once when no HTTP response arrived. A resend can at worst leave
// one extra blank draft, which is better than a create button that never ends.
const CREATE_ATTEMPT_TIMEOUT_MS = 12_000;

const isTransportFailure = (error: unknown): boolean =>
  error instanceof TypeError
  || (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError');

async function postWithTimeout<T>(path: string, body: string, timeoutMs: number): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await request<T>(path, { method: 'POST', body, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export async function createForm(req: CreateFormRequest): Promise<FormDefinition> {
  // Validate the outgoing request before sending — fail loudly on client bugs.
  const validated = CreateFormRequestSchema.parse(req);
  const body = JSON.stringify(validated);
  let raw: unknown;
  try {
    raw = await postWithTimeout<unknown>('/api/forms', body, CREATE_ATTEMPT_TIMEOUT_MS);
  } catch (error) {
    if (!isTransportFailure(error)) throw error;
    try {
      raw = await postWithTimeout<unknown>('/api/forms', body, CREATE_ATTEMPT_TIMEOUT_MS);
    } catch (retryError) {
      if (!isTransportFailure(retryError)) throw retryError;
      throw { status: 0, message: 'Could not reach the server to create the form. Please try again.' } satisfies ApiFailure;
    }
  }
  return FormDefinitionSchema.parse(raw);
}

export async function getForm(formId: string): Promise<FormDefinition> {
  return FormDefinitionSchema.parse(await request<unknown>(`/api/forms/${formId}`));
}

export async function getFormAgentConversation(formId: string): Promise<FormAgentConversationBinding> {
  const raw = await request<unknown>(`/api/forms/${formId}/agent-conversation`);
  return FormAgentConversationBindingSchema.parse(raw);
}

export async function bindFormAgentConversation(
  formId: string,
  conversationId: string,
): Promise<FormAgentConversationBinding> {
  const raw = await request<unknown>(`/api/forms/${formId}/agent-conversation`, {
    method: 'PUT',
    body: JSON.stringify({ conversation_id: conversationId }),
  });
  return FormAgentConversationBindingSchema.parse(raw);
}

export async function sendCommandBatch(definition: FormDefinition, operationId: string, commands: Command[]): Promise<ReducerResult> {
  return request<ReducerResult>(`/api/forms/${definition.form_id}/commands`, {
    method: 'POST',
    body: JSON.stringify({
      schema_version: 1,
      form_id: definition.form_id,
      base_draft_version: definition.draft_version,
      operation_id: operationId,
      commands,
    }),
  });
}

export async function applyAuthorizedAgentBatch(
  formId: string,
  requestValue: AgentApplyRequest,
): Promise<AgentApplyResponse> {
  const validated = AgentApplyRequestSchema.parse(requestValue);
  const raw = await request<unknown>(`/api/forms/${formId}/agent-commands`, {
    method: 'POST',
    body: JSON.stringify(validated),
  });
  return AgentApplyResponseSchema.parse(raw);
}

export interface OwnerSubmissionSummary {
  submission_id: string;
  revision_id: string;
  submitted_at: string;
  answers: Array<{
    block_id: string;
    label: string;
    value: string;
  }>;
}

export async function listSubmissions(formId: string): Promise<OwnerSubmissionSummary[]> {
  return request<OwnerSubmissionSummary[]>(`/api/forms/${formId}/submissions?limit=100`);
}

export function submissionsCsvUrl(formId: string): string {
  return `${API_URL}/api/forms/${formId}/submissions.csv`;
}

// Exported for unit testing only.
export { _normalizeErrorMessage };
